import React, { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import AtlassianIntelligenceIcon from '@atlaskit/icon/core/atlassian-intelligence';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import {
	ConvoAIResponseState,
	type ConvoAIStreamError,
	type ConvoAIStreamingState,
	useConvoAiStreamingLazy,
	type UserConvoAIStreamingConfig,
} from '@atlassian/ai-agent-streaming';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import type { AiFeatureRequestEnvelopeConfluenceConciseSummaryRequest } from '@atlassian/convo-ai-api';

import { useProductDependencies } from '../../../../controllers/use-product-dependencies';

import { messages } from './messages';

const buttonContainerStyles = xcss({
	border: `1px solid ${token('color.border')}`,
	width: 'fit-content',
	marginTop: 'space.075',
	marginBottom: 'space.075',
	borderRadius: 'border.radius.200',
});

export type SummaryRequestBuilderType = {
	contentAri: string;
	locale: string;
};

const buildSummaryRequest = ({
	contentAri,
	locale,
}: SummaryRequestBuilderType): [
	AiFeatureRequestEnvelopeConfluenceConciseSummaryRequest,
	UserConvoAIStreamingConfig,
] => {
	return [
		{
			ai_feature_input: {
				content_ari: contentAri,
				locale,
				character_max: 95,
			},
		},
		{
			apiPath: 'v2/ai-feature/confluence-concise-summary',
			delayChunkUpdate: { gap: 10, time: 20 },
			headers: {
				'x-experience-id': 'confluence-concise-summary',
				'x-product': 'confluence',
			},
		},
	];
};

type AIDescriptionButtonProps = {
	pageAri: string;
	confluencePageId: string;
	isAILoading?: boolean;
	setIsAILoading: Dispatch<SetStateAction<boolean | undefined>>;
	setAIError: Dispatch<SetStateAction<ConvoAIStreamError | undefined>>;
	setAIDescriptionContent: Dispatch<SetStateAction<string | undefined | null>>;
	setAIResponseState: Dispatch<SetStateAction<ConvoAIResponseState | undefined>>;
	analyticsSource: string;
};

type AIDescriptionStreamResponse = {
	content: string;
};

export const AIDescriptionButton = ({
	pageAri,
	confluencePageId,
	isAILoading,
	setIsAILoading,
	setAIError,
	setAIDescriptionContent,
	setAIResponseState,
	analyticsSource,
}: AIDescriptionButtonProps) => {
	const { aiDescriptionExperienceTracker } = useProductDependencies();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { locale } = useIntl();

	const { trackAIInteractionInit, trackAIResultView, trackAIResultError } =
		useAIEventsInstrumentation();
	const onStateChange = useCallback(
		(state: ConvoAIStreamingState<AIDescriptionStreamResponse>) => {
			switch (state.responseState) {
				case ConvoAIResponseState.Error:
					if (state.error) {
						const { status_code: aiErrorCode, message_template: aiErrorMessage } =
							state.error.message;
						trackAIResultError({
							aiErrorCode,
							aiErrorMessage,
						});
					}
					break;
				case ConvoAIResponseState.FinalResponse:
					if (state.content) {
						trackAIResultView();
					}
					break;
				default:
					break;
			}
		},
		[trackAIResultError, trackAIResultView],
	);
	const [request, config] = useMemo(
		() =>
			buildSummaryRequest({
				contentAri: pageAri,
				locale,
			}),
		[locale, pageAri],
	);

	const [{ content, responseState, error }, fetchAI] = useConvoAiStreamingLazy<
		AiFeatureRequestEnvelopeConfluenceConciseSummaryRequest,
		AIDescriptionStreamResponse
	>(config, onStateChange);

	const startAIDescription = useCallback(() => {
		trackAIInteractionInit();
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'aiDescription',
				source: analyticsSource,
			},
		}).fire();
		aiDescriptionExperienceTracker?.start({ analyticsSource, confluencePageId });

		setIsAILoading(true);
		fetchAI(request);
	}, [
		fetchAI,
		request,
		trackAIInteractionInit,
		confluencePageId,
		createAnalyticsEvent,
		aiDescriptionExperienceTracker,
		analyticsSource,
		setIsAILoading,
	]);

	useEffect(() => {
		setAIResponseState(responseState);
		setAIDescriptionContent(content);
		setAIError(error);
	}, [responseState, content, error, setAIResponseState, setAIDescriptionContent, setAIError]);

	return (
		<Box xcss={buttonContainerStyles}>
			<Button
				appearance="subtle"
				onClick={startAIDescription}
				isDisabled={isAILoading}
				iconBefore={AtlassianIntelligenceIcon}
			>
				<FormattedMessage {...messages.suggestDescriptionLabel} />
			</Button>
		</Box>
	);
};
